<template>
  <div>
    <main-layout
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
      <div class="container">
        <grid-row class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <div id="header">
              <h1 class="header-title">
                <chevron
                  @click="$router.push({name: 'Workspace'})"/>
                {{ $t('health.menu.challengeManagement') }}
              </h1>
            </div>
            <alert
              v-if="alertVisible"
              :message="alertMessage"
              :type="alertType">
            </alert>
          </grid-col>
        </grid-row>
        <grid-row column-align="center">
          <grid-col col-size="2">
            <h2>{{ $t('health.challenge.templates') }}</h2>
          </grid-col>
          <grid-col col-size="2">
            <div class="header-add-button">
              <router-link to="/add-challenge-template">
                  <span
                    v-tooltip.left.top-start="$t('health.challenge.newTemplate')">
                    <font-awesome-icon
                      class="header-add-button-icon"
                      :icon="icon.add"/>
                  </span>
              </router-link>
            </div>
          </grid-col>
        </grid-row>
        <grid-row column-align="center">
          <grid-col col-size="4">
            <table-component
              ref="templatesTable"
              :data="fetchTemplates"
              :show-caption="false"
              class="datatable"
              :show-filter="true">
              <table-column show="id" label="Id" :hidden="true"></table-column>
              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <span
                    v-tooltip.left.top-start="$t('health.challenge.launchChallenge')">
                    <font-awesome-icon
                      v-on:click="onLaunchChallenge(row)"
                      :icon="icon.launch"
                      class="table-iconset-icon"/>
                  </span>
                </template>
              </table-column>

              <table-column
                show="name"
                :label="$t('health.challenge.name')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>
              <table-column
                show="nameEng"
                :label="$t('health.challenge.nameEng')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>

              <table-column
                show="type"
                :label="$t('health.challenge.type')"
                :formatter="formatType"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>
              <table-column
                show="category"
                :formatter="formatCategory"
                :label="$t('health.challenge.category')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>
              <table-column
                show="description"
                :label="$t('health.challenge.description')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>
              <table-column
                show="descriptionEng"
                :label="$t('health.challenge.descriptionEng')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>

              <table-column
                show="formula"
                :label="$t('health.challenge.formula')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>

              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    v-on:click="onDeleteTemplate(row)"
                    :icon="icon.trash"
                    class="table-iconset-icon"/>
                </template>
              </table-column>
            </table-component>
          </grid-col>
        </grid-row>
        <grid-row class="margin-top-l" column-align="center">
          <grid-col col-size="3">
            <h2>{{ $t('health.challenge.activeAndFutureChallenges') }}</h2>
          </grid-col>
          <grid-col col-size="1">
            <div class="header-add-button">
              <router-link to="/challenge/history">
                  <span v-tooltip.left.top-start="$t('health.challenge.history')">
                    <font-awesome-icon
                      class="header-add-button-icon"
                      :icon="icon.history"/>
                  </span>
              </router-link>
            </div>
          </grid-col>
        </grid-row>
        <grid-row class="margin-bottom-xxl" column-align="center">
          <grid-col col-size="4">
            <table-component
              ref="activeChallengesTable"
              :data="fetchActiveChallenges"
              :show-caption="false"
              class="datatable"
              :show-filter="true">
              <table-column show="id" label="Id" :hidden="true"></table-column>
              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    v-on:click="displayChallengeModal(row)"
                    :icon="icon.info"
                    class="table-iconset-icon"/>
                </template>
              </table-column>
              <table-column
                show="name"
                :label="$t('health.challenge.name')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>
              <table-column
                show="nameEng"
                :label="$t('health.challenge.nameEng')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>

              <table-column
                show="type"
                :label="$t('health.challenge.type')"
                :formatter="formatType"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>

              <table-column
                show="category"
                :formatter="formatCategory"
                :label="$t('health.challenge.category')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>

              <table-column
                show="startDate"
                :label="$t('health.challenge.startDate')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>

              <table-column
                show="endDate"
                :label="$t('health.challenge.endDate')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>

              <table-column
                show="participants"
                :label="$t('health.challenge.participants')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>

              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    v-on:click="displayChallengeParticipantsModal(row)"
                    :icon="icon.user"
                    class="table-iconset-icon"/>
                </template>
              </table-column>
            </table-component>
          </grid-col>
        </grid-row>
      </div>
      <challenge-data-modal
        v-if="showChallengeModal"
        :challenge="selectedChallenge"
        v-on:close="closeChallengeModal"
      />
      <launch-challenge-modal
        v-if="showLaunchChallengeModal"
        :template="selectedTemplate"
        v-on:save="onChallengeCreated"
        v-on::error="onChallengeCreatedError"
        v-on:close="closeLaunchChallengeModal"
      />
      <ok-cancel-modal
        v-if="showConfirmationDeleteModal"
        v-on:ok="onDeleteTemplateConfirm"
        v-on:cancel="onDeleteTemplateCancel"
        :title="deleteModalMessage"
        :message="deleteModalMessage"
        :okButton="$t('commons.delete')"
        :cancelButton="$t('commons.cancel')">
      </ok-cancel-modal>

      <challenge-participants-modal
        v-if="showChallengeParticipantsModal"
        :challenge="selectedChallenge"
        v-on:close="closeChallengeParticipantsModal"
      ></challenge-participants-modal>
    </main-layout>
  </div>
</template>

<script>
import MainLayout from '@/layouts/MainLayout.vue';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import Chevron from '@/components/shared/Chevron';
import TableComponent from '@/components/shared/table/components/TableComponent';
import TableRow from '@/components/shared/table/components/TableRow';
import TableColumn from '@/components/shared/table/components/TableColumn';
import pagination from '@/components/shared/table/pagination';
import challengeApi from 'BRAND/api/challenge';
import Alert from '@/components/shared/alert/Alert';
import ChallengeDataModal from 'BRAND/components/challenge/ChallengeDataModal';
import LaunchChallengeModal from 'BRAND/components/challenge/LaunchChallengeModal';
import ChallengeParticipantsModal from 'BRAND/components/challenge/ChallengeParticipantsModal';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {faPlusCircle} from '@fortawesome/pro-solid-svg-icons';
import {
  faInfoCircle, faTrashAlt, faRocketLaunch, faUser, faHistory
} from '@fortawesome/pro-regular-svg-icons';
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';

export default {
  name: 'ChallengeManagement',
  components: {
    MainLayout,
    GridCol,
    GridRow,
    Chevron,
    TableComponent,
    TableRow,
    TableColumn,
    Alert,
    ChallengeDataModal,
    LaunchChallengeModal,
    FontAwesomeIcon,
    OkCancelModal,
    ChallengeParticipantsModal
  },
  data () {
    return {
      activeChallenges: [],
      templates: [],
      showChallengeModal: false,
      showLaunchChallengeModal: false,
      showConfirmationDeleteModal: false,
      showChallengeParticipantsModal: false,
      deleteModalMessage: '',
      selectedChallenge: null,
      selectedTemplate: null,
      templateToDelete: null,
      icon: {
        info: faInfoCircle,
        add: faPlusCircle,
        trash: faTrashAlt,
        launch: faRocketLaunch,
        user: faUser,
        history: faHistory
      },
      /* Table */
      tablePageSize: 5,
      tableState: {
        filter: '',
        page: 1,
        sort: {
          order: '',
          fieldName: ''
        }
      },
      /* Alerts */
      alertVisible: false,
      alertMessage: '',
      alertType: ''
    };
  },
  created () {
    this.requestData();
  },
  watch: {
    getTemplates: {
      handler: function () {
        this.$refs.templatesTable.refresh();
      }
    },
    getActiveChallenges: {
      handler: function () {
        this.$refs.activeChallengesTable.refresh();
      }
    }
  },
  computed: {
    getBreadcrumb () {
      const self = this.$t('health.menu.challengeManagement');
      return [
        {name: this.$t('menu.workspace'), link: '/workspace'},
        {name: self, link: '/challenge-management', disabled: true}
      ];
    },
    getTemplates () {
      return this.templates;
    },
    getActiveChallenges () {
      return this.activeChallenges;
    }
  },
  methods: {
    requestData () {
      challengeApi.getActiveChallenges(this).then(data => {
        this.activeChallenges = data;
      }).catch(() => {
        // TODO (Display error)
      });
      challengeApi.getTemplateChallenges(this).then(data => {
        this.templates = data;
      }).catch(() => {
        // TODO (Display error)
      });
    },
    fetchTemplates ({page, filter, sort}) {
      page = page === null || isNaN(page) ? 1 : page;
      const tablePage = pagination.createPage(
        this.getTemplates,
        this.tableState,
        ['name', 'nameEng', 'formula'],
        this.tablePageSize,
        {page, filter, sort});
      this.tableState = tablePage.state;
      return tablePage.result;
    },
    fetchActiveChallenges ({page, filter, sort}) {
      page = page === null || isNaN(page) ? 1 : page;
      const tablePage = pagination.createPage(
        this.getActiveChallenges,
        this.tableState,
        ['name', 'nameEng'],
        this.tablePageSize,
        {page, filter, sort});
      this.tableState = tablePage.state;
      return tablePage.result;
    },
    displayChallengeModal (challenge) {
      this.selectedChallenge = challenge;
      this.showChallengeModal = true;
    },
    displayChallengeParticipantsModal (challenge) {
      this.selectedChallenge = challenge;
      this.showChallengeParticipantsModal = true;
    },
    closeChallengeModal () {
      this.showChallengeModal = false;
      this.selectedChallenge = null;
    },
    closeChallengeParticipantsModal () {
      this.showChallengeParticipantsModal = false;
      this.selectedChallenge = null;
    },
    closeLaunchChallengeModal () {
      this.showLaunchChallengeModal = false;
      this.selectedTemplate = null;
    },
    onDeleteTemplate (template) {
      this.templateToDelete = template;
      this.deleteModalMessage = this.$t('health.challenge.deleteTemplateModalMessage');
      this.showConfirmationDeleteModal = true;
    },
    onDeleteTemplateConfirm () {
      const id = this.templateToDelete.id;
      this.templateToDelete = {};
      this.showConfirmationDeleteModal = false;
      challengeApi.deleteTemplate(this, id).then(() => {
        showAlert(this, this.$t('health.challenge.successDeleteTemplate'), 'success');
        this.requestData();
      }, () => {
        showAlert(this, this.$t('health.license.errorDeleteTemplate'), 'error');
      });
    },
    onDeleteTemplateCancel () {
      this.templateToDelete = {};
      this.showConfirmationDeleteModal = false;
    },
    onLaunchChallenge (template) {
      this.selectedTemplate = template;
      this.showLaunchChallengeModal = true;
    },
    onChallengeCreated () {
      showAlert(this, this.$t('health.challenge.successChallengeCreated'), 'success');
      this.showLaunchChallengeModal = false;
      this.requestData();
    },
    onChallengeCreatedError () {
      showAlert(this, this.$t('health.challenge.errorChallengeCreated'), 'error');
      this.showLaunchChallengeModal = false;
    },
    formatType (value) {
      return value != null ? this.$t('health.challenge.types.' + value) : '';
    },
    formatCategory (value) {
      return value != null ? this.$t('health.challenge.categories.' + value) : '';
    }
  }
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 2000);
};
</script>

<style lang="scss">
@import "src/stylesheets/variables";

#header {
  display: inline-block;
  width: 100%;
  line-height: 1rem;
}

.header-title {
  float: left;
}

.header-add-button {
  float: right;
}

.header-add-button-icon {
  font-size: 3rem;
  color: $secondary;
}
</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/challenge/ChallengeManagement.vue